import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tabs, Tab, Box, Typography, TextField, Alert,
  CircularProgress, Paper, Divider
} from '@mui/material';
import DiffViewer from './DiffViewer';
import { htmlToText } from '../../../utils/htmlText';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`review-tabpanel-${index}`}
      aria-labelledby={`review-tab-${index}`}
      {...other}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && (
        <Box sx={{ p: 2, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

/**
 * ReviewFeedbackDialog component - Shows AI review feedback and recommendations for a requirement
 * 
 * @component
 */
const ReviewFeedbackDialog = ({
  open,
  onClose,
  data,
  originalContent,
  onApplyRecommendations
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [editedRecommendation, setEditedRecommendation] = useState('');
  const [loading, setLoading] = useState(true);

  // Initialize edited recommendation when data changes
  useEffect(() => {
    if (data?.recommendation) {
      // Ensure recommendation is converted to string, handling both string and array cases
      const recommendationText = Array.isArray(data.recommendation) 
        ? data.recommendation.join('\n') 
        : String(data.recommendation);
      setEditedRecommendation(recommendationText);
    } else {
      setEditedRecommendation('');
    }
    
    // When data is loaded, set loading to false
    if (data) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [data]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Apply edited recommendation
  const handleApplyRecommendations = () => {
    if (onApplyRecommendations && editedRecommendation) {
      onApplyRecommendations(editedRecommendation);
    }
  };

  // Render feedback section
  const renderFeedbackItem = (title, content) => {
    if (!content) return null;
    
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" component="p" sx={{ ml: 1, mt: 0, mb: 2 }}>
          {content}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { height: '80vh', display: 'flex', flexDirection: 'column' } },
      }}
    >
      <DialogTitle>Requirement Review</DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Feedback" />
          <Tab label="Edit Recommendation" />
          <Tab label="Diff View" />
        </Tabs>
      </Box>
      
      <DialogContent dividers sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : !data ? (
          <Alert severity="error" sx={{ m: 2 }}>
            No data returned from AI service. Please try again later.
          </Alert>
        ) : (
          <>
            {/* Feedback Tab */}
            <TabPanel value={tabValue} index={0}>
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, maxHeight: 'calc(100% - 16px)', overflow: 'auto' }}
              >
                {/* Original requirement shown at the top for context (T1.9 #1).
                    Rendered as readable text (HTML stripped) — never raw markup. */}
                {originalContent && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Original Requirement
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1, whiteSpace: 'pre-wrap' }}>
                      {htmlToText(originalContent)}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                  </Box>
                )}

                {renderFeedbackItem('Clarity', data.clarity)}
                {renderFeedbackItem('Completeness', data.completeness)}
                {renderFeedbackItem('Consistency', data.consistency)}
                {renderFeedbackItem('Correctness', data.correctness)}
                {renderFeedbackItem('Conciseness', data.conciseness)}
                {renderFeedbackItem('Robustness', data.robustness)}
                {renderFeedbackItem('Other Issues', data.other)}
                
                {data.recommendation && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Recommended Improvement
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {Array.isArray(data.recommendation) 
                        ? data.recommendation.join('\n') 
                        : data.recommendation}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </TabPanel>
            
            {/* Edit Recommendation Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Edit the recommended text before applying:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  value={editedRecommendation}
                  onChange={(e) => setEditedRecommendation(e.target.value)}
                  minRows={15}
                  maxRows={20}
                  sx={{ 
                    flex: 1,
                    '.MuiInputBase-root': {
                      height: '100%'
                    },
                    '.MuiInputBase-input': {
                      height: '100% !important'
                    }
                  }}
                />
              </Box>
            </TabPanel>
            
            {/* Diff View Tab */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="subtitle2" gutterBottom>
                Compare original text with recommendation:
              </Typography>
              {/* Diff readable text, not raw HTML markup (T1.9 #5). */}
              <DiffViewer
                oldText={htmlToText(originalContent)}
                newText={htmlToText(editedRecommendation)}
              />
            </TabPanel>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleApplyRecommendations}
          variant="contained"
          color="primary"
          disabled={loading || !data || tabValue !== 1}
        >
          Apply Recommendation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ReviewFeedbackDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  originalContent: PropTypes.string,
  onApplyRecommendations: PropTypes.func.isRequired
};

export default ReviewFeedbackDialog;